import { useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Locate, Loader2 } from 'lucide-react';

// Custom icon for user location
const userLocationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// User location circle style
const userCircleStyle = {
  color: '#3b82f6',
  fillColor: '#3b82f6',
  fillOpacity: 0.15,
  weight: 2
};

export function MapLocationButton({ language = 'es' }) {
  const map = useMap();
  const [loading, setLoading] = useState(false);
  const [userMarker, setUserMarker] = useState(null);
  const [userCircle, setUserCircle] = useState(null);

  const t = {
    locateMe: language === 'es' ? 'Mi ubicación' : 'My location',
    locating: language === 'es' ? 'Localizando...' : 'Locating...',
    locationError: language === 'es' ? 'No se pudo obtener tu ubicación' : 'Could not get your location',
    youAreHere: language === 'es' ? 'Estás aquí' : 'You are here',
    accuracy: language === 'es' ? 'Precisión' : 'Accuracy',
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      alert(language === 'es' ? 'Tu navegador no soporta geolocalización' : 'Your browser does not support geolocation');
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        
        // Remove previous markers if they exist
        if (userMarker) {
          map.removeLayer(userMarker);
        }
        if (userCircle) {
          map.removeLayer(userCircle);
        }

        // Add accuracy circle
        const circle = L.circle([latitude, longitude], {
          ...userCircleStyle,
          radius: accuracy
        }).addTo(map);

        // Add user marker
        const marker = L.marker([latitude, longitude], { icon: userLocationIcon })
          .addTo(map)
          .bindPopup(`
            <div class="font-['Outfit'] text-center">
              <p class="font-bold text-[#252a34] mb-1">${t.youAreHere}</p>
              <p class="text-xs text-[#252a34]/60">${t.accuracy}: ~${Math.round(accuracy)}m</p>
            </div>
          `)
          .openPopup();

        setUserMarker(marker);
        setUserCircle(circle);

        // Fly to user location
        map.flyTo([latitude, longitude], 14, {
          duration: 1.5
        });

        setLoading(false);
      },
      (error) => {
        setLoading(false);
        let errorMsg = t.locationError;
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = language === 'es' 
              ? 'Permiso de ubicación denegado. Actívalo en la configuración del navegador.' 
              : 'Location permission denied. Enable it in browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = language === 'es' 
              ? 'Información de ubicación no disponible.' 
              : 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMsg = language === 'es' 
              ? 'Tiempo de espera agotado.' 
              : 'Location request timed out.';
            break;
          default:
            break;
        }
        
        alert(errorMsg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return (
    <button
      onClick={handleLocate}
      disabled={loading}
      className="absolute top-20 left-2.5 z-[1000] bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-md shadow-md px-3 py-2 flex items-center gap-2 text-sm font-medium text-[#252a34] transition-colors disabled:opacity-70"
      title={t.locateMe}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          <span className="hidden sm:inline">{t.locating}</span>
        </>
      ) : (
        <>
          <Locate className="w-4 h-4 text-blue-500" />
          <span className="hidden sm:inline">{t.locateMe}</span>
        </>
      )}
    </button>
  );
}
